import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { getResolvedLocale, type ResolvedLocale } from "../../app/locale.js";
import { TRANSLATION_TIMEOUT_MS } from "../../app/i18nLifecycle.js";
import { finishStartup, startupError } from "../../lib/startupTelemetry.js";
import { StartupScreen } from "./StartupScreen.js";

export function ViewFallback({ failed = false, translations = false, locale = getResolvedLocale() }: {
  failed?: boolean; translations?: boolean; locale?: ResolvedLocale;
}) {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (failed) return;
    const timer = setTimeout(() => setExpired(true), TRANSLATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [failed]);
  const failure = failed || expired;
  useEffect(() => {
    if (!failure) return;
    startupError(translations ? expired ? "i18n_timeout" : "i18n_failed" : "route_failed");
    finishStartup("error");
  }, [failure, expired, translations]);
  return <StartupScreen embedded locale={locale} failed={failure} onRetry={() => window.location.reload()} />;
}

/** Start locale and JS requests together, but never commit a blank/raw-key view. */
export function RouteLoadingBoundary({ namespace, preload, children }: {
  namespace: string; preload: () => Promise<unknown>; children: ReactNode;
}) {
  const { i18n, ready } = useTranslation(namespace);
  useEffect(() => { void preload().catch(() => undefined); }, [preload]);
  // i18next considers failed backend reads "loaded". Require actual resources,
  // because ready alone otherwise reveals a view after a failed locale request.
  const locale = i18n.language || "en";
  const usable = ready && [locale, "en"].some((language) => {
    const bundle = i18n.getResourceBundle(language, namespace);
    return bundle && Object.keys(bundle).length > 0;
  });
  if (!usable) return <ViewFallback key={`${namespace}:${locale}`} locale={locale.startsWith("es") ? "es" : "en"} translations failed={ready} />;
  return children;
}
